import { useRef } from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X } from 'lucide-react'
import { SITE, whatsappHref } from '../../data/site'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { revealDialog } from '../../animations/gsapAnimations'
import type { Vehicle } from '../../data/vehicles'
import { nameOf } from '../../data/vehicles'
import Button from '../ui/Button'

/* Pakistani mobile numbers, however people actually write them: 03001234567,
   +92 300 1234567, 0300-1234567. */
const PHONE = /^(\+92|0)?[\s-]?3\d{2}[\s-]?\d{7}$/

const schema = z.object({
  name: z.string().trim().min(2, 'Please tell us your name'),
  phone: z.string().trim().regex(PHONE, 'Enter a valid mobile number'),
  when: z.string().min(1, 'Pick a day that suits you'),
  notes: z.string().trim().max(300).optional(),
})

type TestDriveForm = z.infer<typeof schema>

const DAYS = [
  'Today',
  'Tomorrow',
  'This weekend',
  'Next week',
  'I will call to arrange',
] as const

type TestDriveModalProps = {
  vehicle: Vehicle
  open: boolean
  onClose: () => void
}

function TestDriveModal({ vehicle, open, onClose }: TestDriveModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, open)

  /* Enter-only, for the reason page transitions are: an exit tween holds a
     dismissed dialog on screen after the user has closed it. Keyed on `open`
     because the panel is unmounted while closed. */
  const dialogRef = useGsapContext<HTMLDivElement>(
    (scope) => {
      const panel = scope.querySelector('[data-dialog="panel"]')
      if (panel) revealDialog(panel, scope.querySelector('[data-dialog="backdrop"]'))
    },
    [open],
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TestDriveForm>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', phone: '', when: 'This weekend', notes: '' },
  })

  /* There is no booking endpoint yet — the Express app in /server is still a
     stub — so rather than pretending to file the request, the form composes
     it and hands it to WhatsApp, which is where this dealership's bookings
     actually happen. When the API lands, this handler posts instead and the
     WhatsApp route becomes the fallback. */
  const onSubmit: SubmitHandler<TestDriveForm> = (data) => {
    const lines = [
      `Hi ${SITE.name} ${SITE.suffix}, I would like to book a test drive.`,
      '',
      `Car: ${nameOf(vehicle)} (${vehicle.year})`,
      `Name: ${data.name}`,
      `Phone: ${data.phone}`,
      `When: ${data.when}`,
      data.notes ? `Notes: ${data.notes}` : '',
    ].filter(Boolean)

    window.open(whatsappHref(lines.join('\n')), '_blank', 'noopener')
    onClose()
  }

  if (!open) return null

  const field =
    'mt-2 w-full rounded-xl border border-line bg-raised px-4 py-3 text-[15px] text-platinum placeholder:text-dim focus:border-lime focus:outline-none'
  const label =
    'label-sm text-steel'

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-60 flex items-end justify-center sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-dialog="backdrop"
        className="absolute inset-0 bg-void/70 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        data-dialog="panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="test-drive-title"
        className="relative max-h-[92svh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-line bg-elevated p-6 shadow-pop sm:rounded-2xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="label-sm text-lime-deep">
              Book a test drive
            </p>
            <h2
              id="test-drive-title"
              className="mt-3 font-display text-2xl font-semibold tracking-[-0.03em] text-platinum"
            >
              {nameOf(vehicle)}
            </h2>
            <p className="mt-1 text-[13.5px] text-steel">
              {vehicle.year > 0 ? vehicle.year : 'Model year on request'}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-10 shrink-0 place-items-center rounded-lg border border-line text-silver transition hover:border-lime"
          >
            <X aria-hidden="true" strokeWidth={1.8} className="size-4.5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 flex flex-col gap-5" noValidate>
          <div>
            <label htmlFor="td-name" className={label}>
              Your name
            </label>
            <input id="td-name" {...register('name')} className={field} placeholder="Full name" />
            {errors.name ? (
              <p role="alert" className="mt-2 text-[12.5px] text-mint-deep">
                {errors.name.message}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="td-phone" className={label}>
              Mobile number
            </label>
            <input
              id="td-phone"
              type="tel"
              inputMode="tel"
              {...register('phone')}
              className={field}
              placeholder="0300 1234567"
            />
            {errors.phone ? (
              <p role="alert" className="mt-2 text-[12.5px] text-mint-deep">
                {errors.phone.message}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="td-when" className={label}>
              When suits you
            </label>
            <select id="td-when" {...register('when')} className={field}>
              {DAYS.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="td-notes" className={label}>
              Anything else (optional)
            </label>
            <textarea
              id="td-notes"
              rows={3}
              {...register('notes')}
              className={`${field} resize-none`}
              placeholder="A colour you want to see, a time that works best…"
            />
          </div>

          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full">
            Send booking on WhatsApp
          </Button>

          <p className="text-[12px] leading-relaxed text-dim">
            This opens WhatsApp with your details written out. Nothing is sent
            until you press send there.
          </p>
        </form>
      </div>
    </div>
  )
}

export default TestDriveModal
